// src/services/failuresService.ts
//
// All Supabase access for the Settings → Failures read surface (Story 4.8) is
// isolated here. This service is READ-ONLY BY DESIGN: failure rows have NO
// mutations at pilot (Decision 9 — no acknowledge/resolve/dismiss/re-send;
// yak_dead_letters is append-only and a `failed` notification is terminal per 3.7).
// Remediation lives on the pointed-to objects (fix the rep's email, respond again
// from the ticket, fix routing/roster) — never a write here.
//
// It reads the FOUR feeds the failure view unifies, each newest-first and capped
// (Decision 10 — client-side reads merged in failureDisplay.ts; a SQL UNION
// view is premature at pilot volume):
//   (a) yak_dead_letters (RLS: 4.6's org-admin SELECT + super-admin-sees-all) — the
//       rep name + live email_bounced_at ride the composite-FK embed (never N+1).
//   (b) yak_notifications channel IN ('email','sms') status='failed' (3.7's
//       terminal-failure feed, widened to the sms transport by 6.1/AD-18)
//   (c) yak_calls disposition='unrecognized_caller' (3.5/3.6's org-admin signal)
//   (d) yak_call_verifications status='locked_out' (6.2's 2FA lockouts, FR-42) —
//       the rep name rides the composite-FK embed, same as (a)
//
// Layering (spine AD-3): components/hooks never touch Supabase; they consume the
// clean src/types/yak.ts shapes. Results are cast once per method at the boundary.
// Reference chain: regionsService → useRegions → RegionManagement.

import { supabase } from '../lib/supabase'
import type { YakCall, YakNotification } from '../types/yak'
import {
  type DeadLetterRowInput,
  FAILURES_FETCH_LIMIT,
  type VerificationRowInput,
} from '../utils/failureDisplay'

// The rep name + live flag ride the composite FK yak_dead_letters_rep_fkey
// (rep_id, organization_id) → yak_reps (id, organization_id) — one query, never
// N+1. email_bounced_at is the deliberate current-state read (the "still flagged"
// line), not attribution (the row is the attribution record).
const DEAD_LETTER_SELECT =
  '*, rep:yak_reps!yak_dead_letters_rep_fkey(name, email_bounced_at)'

// Story 6.2. Columns are listed EXPLICITLY, never `*`: migration 044 withholds
// the SELECT privilege on otp_hash / otp_salt from `authenticated`, so a `*`
// expansion would be a permission error rather than a null. The rep name rides
// the composite FK (rep_id, organization_id) -> yak_reps (id, organization_id),
// the same one-query embed the dead-letter feed uses — never N+1.
const VERIFICATION_SELECT =
  'id, organization_id, vapi_call_id, rep_id, caller_number, expires_at, ' +
  'attempt_count, send_count, status, created_at, updated_at, ' +
  'rep:yak_reps!yak_call_verifications_rep_fkey(name)'

class FailuresService {
  // All dead letters (incl. bounces), newest-first, capped. RLS scopes rows to
  // the org admin (super_admin sees all orgs + org-NULL rows).
  async listDeadLetters(): Promise<DeadLetterRowInput[]> {
    const { data, error } = await supabase
      .from('yak_dead_letters')
      .select(DEAD_LETTER_SELECT)
      .order('created_at', { ascending: false })
      .order('id', { ascending: false })
      .limit(FAILURES_FETCH_LIMIT)

    if (error) throw new Error(error.message)
    return (data as unknown as DeadLetterRowInput[] | null) ?? []
  }

  // Terminal outbox delivery failures (rows that exhausted retries or died
  // instantly), newest-first, capped. RLS (_org_isolation) scopes to the org.
  // Story 6.1 widened this to BOTH outbox transports — sms failures are outbox
  // failures and belong in the same feed (AD-18: same reaction, same failure
  // surface). in_app rows are excluded because they are born `sent`.
  async listFailedNotifications(): Promise<YakNotification[]> {
    const { data, error } = await supabase
      .from('yak_notifications')
      .select('*')
      .in('channel', ['email', 'sms'])
      .eq('status', 'failed')
      .order('created_at', { ascending: false })
      .order('id', { ascending: false })
      .limit(FAILURES_FETCH_LIMIT)

    if (error) throw new Error(error.message)
    return (data as unknown as YakNotification[] | null) ?? []
  }

  // Unrecognized-caller attempts (an adoption/config signal — worded neutrally in
  // the view, never an error), newest-first, capped. RLS scopes to the org.
  async listUnrecognizedCalls(): Promise<YakCall[]> {
    const { data, error } = await supabase
      .from('yak_calls')
      .select('*')
      .eq('disposition', 'unrecognized_caller')
      .order('created_at', { ascending: false })
      .order('id', { ascending: false })
      .limit(FAILURES_FETCH_LIMIT)

    if (error) throw new Error(error.message)
    return (data as unknown as YakCall[] | null) ?? []
  }

  // Caller-verification lockouts (Story 6.2, FR-42 / AC 3): three failed code
  // read-backs on one call. The `.eq('status', 'locked_out')` is belt-and-braces
  // — migration 044's RLS policy already exposes ONLY locked-out rows, so a
  // pending or verified challenge is invisible to the console by construction
  // (and its hash/salt doubly so).
  async listVerificationLockouts(): Promise<VerificationRowInput[]> {
    const { data, error } = await supabase
      .from('yak_call_verifications')
      .select(VERIFICATION_SELECT)
      .eq('status', 'locked_out')
      .order('created_at', { ascending: false })
      .order('id', { ascending: false })
      .limit(FAILURES_FETCH_LIMIT)

    if (error) throw new Error(error.message)
    return (data as unknown as VerificationRowInput[] | null) ?? []
  }
}

export const failuresService = new FailuresService()
